import { IFormHelperProps } from 'telia-front-react';
import { isEmailValid, isPasswordStrong, isPhoneValid } from './ValidationUtil';

export const ERRORED_HELPER_FIELD_ID_SUFFIX = '-invalid';
export const PERSONAL_CODE_FIELD_ID = 'personalCode';
export const PHONE_FIELD_ID = 'phoneNumber';
export const EMAIL_FIELD_ID = 'emailAddress';
export const USERNAME_FIELD_ID = 'username';
export const PW_FIELD_ID = 'password';
export const NEW_PW_FIELD_ID = 'newPassword';
export const CONFIRM_PW_FIELD_ID = 'confirmPassword';

export const getErroredHelperId = (fieldId: string) => fieldId + ERRORED_HELPER_FIELD_ID_SUFFIX;

const baseFieldHelper = (
  translate: (key: string) => string,
  value: string | null,
  dirty: boolean,
  callback?: () => any,
  fieldId?: string,
): IFormHelperProps | undefined => {
  if (!dirty) {
    return undefined;
  }
  if (!value) {
    return { type: 'error', text: translate('error.mandatory_field_is_empty'), id: fieldId ? getErroredHelperId(fieldId) : undefined };
  }
  if (callback) {
    return callback();
  }
  return undefined;
};

export const notEmptyFieldHelper = (
  translate: (key: string) => string,
  value: string | null,
  dirty: boolean,
  fieldId?: string,
): IFormHelperProps | undefined => {
  return baseFieldHelper(translate, value, dirty, undefined, fieldId);
};

export const personalCodeHelper = (
  translate: (key: string) => string,
  value: string | null,
  countryCode: string,
  dirty: boolean,
): IFormHelperProps | undefined => {
  return baseFieldHelper(
    translate,
    value,
    dirty,
    () => {
      if (countryCode === 'EE' && !validatePersonalCode(value)) {
        return { type: 'error', text: translate('pcode.invalid.format'), id: getErroredHelperId(PERSONAL_CODE_FIELD_ID) };
      }
    },
    PERSONAL_CODE_FIELD_ID,
  );
};

export const validatePersonalCode = (value: string | null) => {
  if (!value) {
    return false;
  }
  if (value.charAt(0) === '0') {
    return false;
  }
  if (value.length !== 11) {
    return false;
  }
  const control = getControlNumber(value);
  if (control !== parseInt(value.charAt(10), 10)) {
    return false;
  }

  const year = Number(value.substring(1, 3));
  const month = Number(value.substring(3, 5));
  const day = Number(value.substring(5, 7));
  const birthDate = getBirthday(value);
  return year === birthDate.getFullYear() % 100 && birthDate.getMonth() + 1 === month && day === birthDate.getDate();
};

const getBirthday = (code: string) => {
  let year = parseInt(code.substring(1, 3), 10);
  const month = parseInt(code.substring(3, 5).replace(/^0/, ''), 10) - 1;
  const day = +code.substring(5, 7).replace(/^0/, '');
  const firstNumber = code.charAt(0);

  if (firstNumber === '1' || firstNumber === '2') {
    year += 1800;
  } else if (firstNumber === '3' || firstNumber === '4') {
    year += 1900;
  } else if (firstNumber === '5' || firstNumber === '6') {
    year += 2000;
  } else if (firstNumber === '7' || firstNumber === '8') {
    year += 2100;
  }

  return new Date(year, month, day);
};

const getControlNumber = (code: string) => {
  const multiplier1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 1];
  const multiplier2 = [3, 4, 5, 6, 7, 8, 9, 1, 2, 3];
  let mod;
  let total = 0;

  for (let i = 0; i < 10; ++i) {
    total += +code.charAt(i) * multiplier1[i];
  }
  mod = total % 11;

  total = 0;
  if (mod === 10) {
    for (let i = 0; i < 10; ++i) {
      total += +code.charAt(i) * multiplier2[i];
    }
    mod = total % 11;
    if (10 === mod) {
      mod = 0;
    }
  }
  return mod;
};

export const phoneFieldHelper = (translate: (key: string) => string, value: string, dirty: boolean): IFormHelperProps | undefined => {
  return baseFieldHelper(
    translate,
    value,
    dirty,
    () => {
      if (!isPhoneValid(value.replace('+', ''))) {
        return { type: 'error', text: translate('phone.invalid.format'), id: getErroredHelperId(PHONE_FIELD_ID) };
      }
    },
    PHONE_FIELD_ID,
  );
};

export const emailFieldHelper = (translate: (key: string) => string, value: string, dirty: boolean): IFormHelperProps | undefined => {
  return baseFieldHelper(
    translate,
    value,
    dirty,
    () => {
      if (!isEmailValid(value)) {
        return { type: 'error', text: translate('error.invalid_email'), id: getErroredHelperId(EMAIL_FIELD_ID) };
      }
    },
    EMAIL_FIELD_ID,
  );
};

export const strongPwFieldHelper = (
  translate: (key: string) => string,
  value: string,
  oldValue: string | null,
  dirty: boolean,
): IFormHelperProps | undefined => {
  const helperId = getErroredHelperId(NEW_PW_FIELD_ID);
  return baseFieldHelper(
    translate,
    value,
    dirty,
    () => {
      if (value.length > 16) {
        return { type: 'error', text: translate('field.defaultError'), id: helperId };
      }
      if (oldValue && oldValue === value) {
        return { type: 'error', text: translate('change_password.new_pw_same_as_old'), id: helperId };
      }
      if (!isPasswordStrong(value)) {
        return { type: 'error', text: translate('password.message.weak'), id: helperId };
      }
    },
    NEW_PW_FIELD_ID,
  );
};

export const confirmPwFieldHelper = (translate: (key: string) => string, value: string, secondValue: string, dirty: boolean): IFormHelperProps | undefined => {
  if (!dirty) {
    return undefined;
  }
  if (value !== secondValue) {
    return { type: 'error', text: translate('password.message.no_match'), id: getErroredHelperId(CONFIRM_PW_FIELD_ID) };
  }
  return undefined;
};

export const helperInErrorState = (helper: IFormHelperProps | undefined) => {
  return !!(helper && helper.type === 'error');
};

export const getHelperErrorMessage = (helper: IFormHelperProps | undefined) => {
  if (helperInErrorState(helper)) {
    return helper?.text as string;
  }

  return undefined;
};
